import { Box, Typography } from '@mui/material';
import {
  Node as ReactFlowNode,
  NodeProps,
  NodeResizer,
  NodeResizerProps,
  NodeToolbar,
  Position,
  useStore,
  useUpdateNodeInternals,
} from '@xyflow/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Markdown } from '@/components/Markdown/Markdown';
import { TextEditorProvider } from '@/components/TextEditor/contexts/TextEditor.provider';
import {
  TextEditor,
  TextEditorProps,
} from '@/components/TextEditor/TextEditor';
import {
  CONTENT_EDITABLE_ID,
  TextEditorWrapper as DefaultTextEditorWrapper,
} from '@/components/TextEditor/TextEditor.utils';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { COLORS } from '@/styles/tokens/colors';

import { useIsInteractive } from '../../hooks/useIsInteractive';
import { useResizeAllSelectedNodes } from '../../hooks/useResizeAllSelectedNodes';
import { TextNoteToolbarPlugin } from './TextNoteToolbarPlugin';
import { TextNoteTypographyProvider } from './TextNoteTypographyProvider';

export interface TextNoteNodeData extends Record<string, unknown> {
  notes: string;
  onNodeResizeStart?: (nodeId: string) => void;
  onNodeResizeEnd?: (nodeId: string) => void;
  onDelete?: (nodeId: string) => void;
  onNotesChange?: (nodeId: string, notes: string) => void;
}

export type TextNoteNodeProps = NodeProps<
  ReactFlowNode<TextNoteNodeData, 'textNote'>
>;

const lineStyle: NodeResizerProps['lineStyle'] = {
  zIndex: 1,
  borderColor: COLORS.NAVY[800],
};

const handleStyle: NodeResizerProps['handleStyle'] = {
  zIndex: 1,
  backgroundColor: COLORS.PRIMITIVES.WHITE,
  height: 8,
  width: 8,
  outline: `1px solid ${COLORS.NAVY[800]}`,
};

// TextEditor's edit path always renders its wrapper with a 1px gray border /
// 6px radius (the `hideBorder` prop is only forwarded in the read-only path).
// Force it off so the editor sits flush inside the tile's white wrapper.
function FlushTextEditorWrapper({ children }: { children: React.ReactNode }) {
  return (
    <DefaultTextEditorWrapper hideBorder>{children}</DefaultTextEditorWrapper>
  );
}

export function TextNoteNode({
  id,
  data,
  selected,
  dragging,
  width,
}: TextNoteNodeProps) {
  const { isInteractive } = useIsInteractive();
  const onResize = useResizeAllSelectedNodes(id);
  const updateNodeInternals = useUpdateNodeInternals();
  // The `height` from NodeProps falls back to `measured.height`, which React
  // Flow keeps populated from ResizeObserver readings. That would lock the
  // wrapper to whatever content height was last measured and kill auto-grow
  // after the first measurement. Read the explicit node.height directly from
  // the store so the wrapper only has a fixed height when the user (or a
  // default) has actually set one.
  const explicitHeight = useStore((s) => s.nodeLookup.get(id)?.height);
  const [isEditing, setIsEditing] = useState(false);
  const editingWrapperRef = useRef<HTMLDivElement>(null);

  const resizeHandlesVisible = !!selected && isInteractive && !dragging;
  const isEmpty = !data.notes?.trim();

  // Destructure so callback identities don't churn on every render — `data` is
  // a fresh object each render (spread in useFlowChartProps), but the callbacks
  // themselves are stable (memoized against `dispatch`).
  const { onNotesChange, onDelete, onNodeResizeStart, onNodeResizeEnd } = data;

  // Stash Lexical's current markdown in a ref while the user types — dispatching
  // on every keystroke rebuilds `state.nodes` and re-renders every tile in the
  // diagram, which produces very visible typing lag. We flush on exit-edit and
  // on unmount so the committed value is never lost.
  const pendingNotesRef = useRef<string | null>(null);
  const onNotesChangeRef = useRef(onNotesChange);
  onNotesChangeRef.current = onNotesChange;
  const notesRef = useRef(data.notes);
  notesRef.current = data.notes;
  const hasClearedHeightThisSessionRef = useRef(false);

  const handleEditorUpdate = useCallback(
    (value: string) => {
      pendingNotesRef.current = value;

      // Selection-only editor updates can carry unchanged content. Only treat
      // updates that diverge from the saved notes as a real edit; otherwise we
      // could silently clear a manually resized height.
      if (value === notesRef.current) return;

      // On the first real keystroke/paste of an edit session, dispatch with
      // the current notes value to trigger UPDATE_TEXT_NOTE's height-clear
      // side effect. That makes React Flow's outer .react-flow__node wrapper
      // auto-size to content so the NodeResizer border tracks the tile as it
      // grows. We dispatch the current value (not the pending one) so this
      // doesn't count as a content change — further keystrokes stay ref-only
      // until blur to avoid typing lag.
      if (!hasClearedHeightThisSessionRef.current) {
        hasClearedHeightThisSessionRef.current = true;
        onNotesChangeRef.current?.(id, notesRef.current);
      }
    },
    [id]
  );

  const flushPendingNotes = useCallback(() => {
    const pending = pendingNotesRef.current;
    pendingNotesRef.current = null;
    if (pending === null || pending === notesRef.current) return;
    onNotesChangeRef.current?.(id, pending);
  }, [id]);

  // Flush when focus leaves the note (e.g. clicking a toolbar outside the
  // flow, or tabbing to the trowser's Done button). Without this, clicking
  // Done dispatches save from pending state that hasn't been committed yet,
  // and the edit is lost.
  const handleEditingBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget as Node | null;
      if (next && editingWrapperRef.current?.contains(next)) return;
      flushPendingNotes();
    },
    [flushPendingNotes]
  );

  const handleDelete = useCallback(() => {
    onDelete?.(id);
  }, [id, onDelete]);
  const handleDeleteRef = useRef(handleDelete);
  handleDeleteRef.current = handleDelete;
  // Stable wrapper so the memoized ToolbarPluginComponent below never changes
  // identity — a new identity would remount the Lexical toolbar and drop the
  // editor's focus/selection mid-edit.
  const stableOnDelete = useCallback(() => handleDeleteRef.current(), []);

  const handleResizeStart = useCallback(() => {
    onNodeResizeStart?.(id);
  }, [id, onNodeResizeStart]);

  const handleResizeEnd = useCallback(() => {
    onNodeResizeEnd?.(id);
  }, [id, onNodeResizeEnd]);

  const handleClick = useCallback(() => {
    if (isInteractive && !isEditing) {
      setIsEditing(true);
    }
  }, [isInteractive, isEditing]);

  // Exit edit mode when the node is deselected (pane click, click on another
  // node, edit-mode toggle off). Clicking the toolbar doesn't deselect the
  // node, so edit mode persists through toolbar interactions.
  useEffect(() => {
    if (!selected || !isInteractive) setIsEditing(false);
  }, [selected, isInteractive]);

  // Flush pending notes when leaving edit mode, and on unmount as a safety net
  // (covers Cancel / navigation away while still editing).
  useEffect(() => {
    if (!isEditing) {
      flushPendingNotes();
      hasClearedHeightThisSessionRef.current = false;
    }
  }, [isEditing, flushPendingNotes]);

  useEffect(() => {
    return () => {
      flushPendingNotes();
    };
  }, [flushPendingNotes]);

  // Force a re-measure on content change so stale measured dimensions from
  // before the edit don't stick around.
  useEffect(() => {
    updateNodeInternals(id);
  }, [data.notes, id, updateNodeInternals]);

  // Stabilize the ToolbarPlugin component identity so Lexical doesn't remount
  // the toolbar on every render. Render it via NodeToolbar so it floats above
  // the note outside the wrapper.
  const ToolbarPluginComponent = useMemo<
    TextEditorProps['ToolbarPlugin']
  >(() => {
    return function TextNoteEditorToolbarPlugin() {
      return (
        <NodeToolbar position={Position.Top} isVisible offset={8}>
          <TextNoteToolbarPlugin onDelete={stableOnDelete} />
        </NodeToolbar>
      );
    };
  }, [stableOnDelete]);

  // minHeight drops from 120 (auto floor) to 60 once there's an explicit
  // height so the user can drag a manually resized note below the auto floor.
  const wrapperStyle = {
    width: width ?? 200,
    height: explicitHeight,
    minWidth: 150,
    minHeight: explicitHeight ? 60 : 120,
    backgroundColor: COLORS.PRIMITIVES.WHITE,
    border: `1px solid ${selected ? COLORS.NAVY[800] : COLORS.NAVY[300]}`,
    borderRadius: '2px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
  } as const;

  // Strip TextEditor's default min-height/padding so both the edit and
  // read-only renders sit flush in the wrapper. Font-size/line-height come
  // from TextNoteTypographyProvider's `text-primary` override. Do NOT force
  // height: 100% on the contenteditable — that would prevent it from pushing
  // the wrapper taller as text is added. Sizing to content is what drives
  // the auto-grow.
  const editorInnerSx = {
    color: COLORS.TEXT.PRIMARY,
    fontWeight: 500,
    borderRadius: 0,
    [`& #${CONTENT_EDITABLE_ID}`]: {
      padding: '0 !important',
      minHeight: '0 !important',
      width: '100% !important',
    },
    // Strip trailing bottom padding on the last element inside the
    // contentEditable so it doesn't inflate measured content height past
    // the container and trigger a spurious scrollbar at low heights.
    // Scrolling is preserved for content that genuinely overflows.
    [`& #${CONTENT_EDITABLE_ID} > :last-child`]: {
      paddingBottom: '0 !important',
    },
  };

  return (
    <LuminaryThemeProvider>
      <TextNoteTypographyProvider>
        <TextEditorProvider theme="userGeneratedContent">
          <NodeResizer
            nodeId={id}
            lineStyle={lineStyle}
            handleStyle={handleStyle}
            isVisible={resizeHandlesVisible}
            onResizeStart={handleResizeStart}
            onResize={onResize}
            onResizeEnd={handleResizeEnd}
            minWidth={150}
            minHeight={60}
          />
          {isEditing && isInteractive ? (
            // Editing: render the full Lexical TextEditor with our inline toolbar.
            // `nodrag` on the wrapper prevents ReactFlow from moving the node
            // while the user selects text. Exit edit mode by deselecting the node.
            // Inline `cursor` beats ReactFlow's selected/dragging `move` cursor.
            <Box
              ref={editingWrapperRef}
              className="nowheel nopan nodrag"
              style={{ cursor: 'text' }}
              sx={wrapperStyle}
              onBlur={handleEditingBlur}
            >
              <TextEditor
                initialValue={data.notes}
                initialValueKind="markdown"
                onImmediateUpdate={handleEditorUpdate}
                hideBorder
                innerSx={editorInnerSx}
                TextEditorWrapper={FlushTextEditorWrapper}
                allowTableInsert={false}
                preserveNewLines
                showCompactBlockDropdown
                ToolbarPlugin={ToolbarPluginComponent}
              />
            </Box>
          ) : (
            // Non-editing: render as static markdown. Click enters edit mode;
            // click-and-drag moves the node (ReactFlow handles it on the wrapper).
            <Box
              className="nowheel nopan"
              onClick={handleClick}
              sx={{
                ...wrapperStyle,
                ...(isInteractive
                  ? {
                      '&:hover': {
                        borderColor: COLORS.NAVY[900],
                      },
                    }
                  : {}),
              }}
            >
              {isEmpty ? (
                <Typography
                  variant="body-s"
                  sx={{
                    color: COLORS.TEXT.PRIMARY,
                    fontWeight: 500,
                  }}
                >
                  Add a custom note
                </Typography>
              ) : (
                <Markdown markdown={data.notes} innerSx={editorInnerSx} />
              )}
            </Box>
          )}
        </TextEditorProvider>
      </TextNoteTypographyProvider>
    </LuminaryThemeProvider>
  );
}
